import { CSSProperties, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Logger from "Logger";
import PostAuthRoutes from "./PostAuthRoutes";
import useUser from 'Hooks/useUser';
import useOrganization from 'Hooks/useOrganization';
import useBrand from 'Hooks/useBrand';
import { useHistory } from "react-router-dom";
import Navbar from "components/Navbar/Navbar";
import UpgradeModalProvider from "Hooks/UpgradeModalContext";
import NavbarProvider from "pages/MarketingPage/Data/NavbarProvider";
import useScreen from "Hooks/useScreen";
import LoaderOverlay from "elements/LoaderOverlay";
import useOrganizationV2 from "Hooks/useOrganizationV2";
import { CurrentBrand, OrganizationInvitationStatus, OrganizationStatus } from "modules/Types";
import { styx } from "modules/Utils";
import DebugBox from "components/shareable/DebugBox";
import Axios from "helpers/Interceptor";
import useMaestroChat from "Hooks/useMaestroChat";

const InitializeApp = () => {
    const history = useHistory();
    const {
        isAuthenticated: isAuthenticatedWithAuth0,
        isLoading: isAuth0Loading,
        logout,
        loginWithRedirect: redirectToAuth0LoginPage,
        getAccessTokenSilently,
        user: userAuth0,
    } = useAuth0();

    const {
        currentUser,
        getCurrentUser,
        isLoadingUser,
    } = useUser();

    const {
        currentBrand,
        getBrands,
        isLoadingBrands,
        loadedBrands,
    } = useBrand();

    const {
        // organization,
        // isOrganizationInitialized,
        getOrganization: getOrganization_OLD, // TODO CONNER: only used for billing tab right now
    } = useOrganization();

    const {
        organization,
        isLoadingOrganization: isLoadingOrganizationV2,
        isOrganizationInitialized, // TODO CONNER: put back when using V2!
        getOrganization: getOrganizationV2,
        checkOrganizationUserInvitationStatus,
        setInvitationStatus,
    } = useOrganizationV2();

    const { resumeChat, setActiveWorkspace } = useMaestroChat();
    const { cssCalc } = useScreen();

    /** Handles linking anonymous brand to authenticated user after login */
    const handleAnonymousBrandLinking = async (currentUserLocal: any) => {
        try {
            // Check for stored session ID from pre-auth
            const preAuthSessionId = localStorage.getItem('pre-auth-session-id');
            if (!preAuthSessionId || !currentUserLocal?.id) {
                return; // No anonymous brand to link
            }

            console.log('🔗 Linking anonymous brand to authenticated user:', currentUserLocal.id);
            
            // Call backend API to link anonymous brand to user
            const response = await Axios.post('/temporary-brand/link-to-user', {
                sessionId: preAuthSessionId,
                userId: currentUserLocal.id
            });

            if (response.data?.brand) {
                console.log('✅ Successfully linked anonymous brand:', response.data.brand);
                
                // Set as first session since user came from anonymous flow
                const { setIsFirstSession } = useBrand.getState();
                setIsFirstSession(true);
                console.log('🎯 Set isFirstSession=true for user who came from anonymous flow');
                
                // Handle post-auth chat and workspace restoration
                await handlePostAuthRestoration();
                
                // Clean up localStorage
                localStorage.removeItem('pre-auth-session-id');
                localStorage.removeItem('anonymous-session-id'); // Also remove the original session ID
                
            } else {
                console.warn('Brand linking response missing brand data');
            }
        } catch (error) {
            console.error('Failed to link anonymous brand to user:', error);
            // Don't throw - we want initialization to continue even if linking fails
        }
    };

    /** Handles restoring chat and workspace state after authentication */
    const handlePostAuthRestoration = async () => {
        try {
            // Check for stored chat and workspace IDs
            const preAuthChatId = localStorage.getItem('pre-auth-chat-id');
            const preAuthCampaignId = localStorage.getItem('pre-auth-campaign-id');
            const preAuthContentAnalysisId = localStorage.getItem('pre-auth-content-analysis-id');
            const preAuthMarketingPlanId = localStorage.getItem('pre-auth-marketing-plan-id');
            
            if (preAuthChatId) {
                console.log('🔄 Restoring chat session:', preAuthChatId);
                
                // Resume the chat conversation
                await resumeChat(preAuthChatId);
                
                // Restore workspace based on what was stored
                if (preAuthCampaignId) {
                    console.log('🔄 Restoring campaign workspace:', preAuthCampaignId);
                    setActiveWorkspace({
                        type: 'campaignApproval',
                        data: {
                            campaignId: preAuthCampaignId
                        }
                    });
                } else if (preAuthContentAnalysisId) {
                    console.log('🔄 Restoring content analysis workspace:', preAuthContentAnalysisId);
                    setActiveWorkspace({
                        type: 'contentAnalysis',
                        data: {
                            analysisId: preAuthContentAnalysisId
                        }
                    });
                } else if (preAuthMarketingPlanId) {
                    console.log('🔄 Restoring marketing plan workspace:', preAuthMarketingPlanId);
                    setActiveWorkspace({
                        type: 'marketingPlan',
                        data: {
                            planId: preAuthMarketingPlanId
                        }
                    });
                }
                
                // Navigate to the chat
                const urlParams = new URLSearchParams();
                // Only add autoOpenSubscription for campaign approval
                if (preAuthCampaignId) {
                    urlParams.set('autoOpenSubscription', 'true');
                }
                
                const chatUrl = `/chats/${preAuthChatId}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
                history.push(chatUrl);
                
                // Clean up stored IDs
                localStorage.removeItem('pre-auth-chat-id');
                localStorage.removeItem('pre-auth-campaign-id');
                localStorage.removeItem('pre-auth-content-analysis-id');
                localStorage.removeItem('pre-auth-marketing-plan-id');
                
                return true; // Indicate we handled restoration
            }
        } catch (error) {
            console.error('Failed to restore post-auth state:', error);
        }
        
        return false; // No restoration needed
    };

    /** Initializes the user session with Auth0 and database details */
    const initializeUserSession = async () => {
        // console.log('initializeUserSession >>>>');
        try {
            // Wait for Auth0 to finish loading before making any decisions
            if (isAuth0Loading) {
                return;
            }

            // Only redirect if we're definitely not authenticated
            if (!isAuthenticatedWithAuth0) {
                // console.log('initializeUserSession >>>> not authenticated');
                const params = new URLSearchParams(window.location.search);
                const authCode = params.get('auth_code');

                // If we have a TikTok auth code, prioritize redirecting to the TikTok callback
                if (authCode) {
                    // Store auth_code in localStorage for the callback route to use
                    localStorage.setItem('tt_auth_code', authCode);

                }

                redirectToAuth0LoginPage({
                    redirectUri: `${window.location.origin}`
                });
                return;
            }

            // Add guards against re-initialization
            const userSubAuth0 = userAuth0?.sub;
            if (!userSubAuth0) {
                // console.log('initializeUserSession >>>> isLoadingUser || currentUser');
                return;
            }

            const currentUserLocal = await getCurrentUser(
                true,
                userSubAuth0,
                getAccessTokenSilently,
                logout,
            );

            // Check for anonymous brand linking after authentication
            await handleAnonymousBrandLinking(currentUserLocal);

            const currentBrandLocal = await getBrands(true, true) as CurrentBrand;

            const organizationV2Data = await getOrganizationV2({
                brandId: currentBrandLocal?.id,
                userId: currentUserLocal?.id,
            });
            const organizationId = organizationV2Data?.organization.id
            if (organizationV2Data && organizationId) {
                getOrganization_OLD(organizationId);
                const organizationUsersLocal = organizationV2Data?.organizationUsers;
                const invitationStatusLocal = organizationUsersLocal?.find(ou => ou.user_id === currentUserLocal?.id)?.invitation_status;

                if (invitationStatusLocal === OrganizationInvitationStatus.PENDING) {
                    history.push('/team');
                }
            }
        } catch (err) {
            Logger.error('Error initializing session:', err);
            if (!isAuthenticatedWithAuth0) {
                logout();
            }
        }
    };

    useEffect(() => {
        if (!isAuth0Loading) {
            initializeUserSession();
        }
    }, [isAuth0Loading, isAuthenticatedWithAuth0, logout]);

    useEffect(() => {
        if (!currentUser || !loadedBrands) {
            return;
        }

        if (!currentUser.email) {
            history.push('/email');
            return;
        }

        // Skip /add-artist redirect if user came from anonymous flow and we have restored their session
        const hasPreAuthData = localStorage.getItem('pre-auth-chat-id') || localStorage.getItem('pre-auth-campaign-id');
        
        if (!loadedBrands.length && !hasPreAuthData) {
            history.push('/add-artist');
            return;
        }

        if (organization?.setup_status === OrganizationStatus.WAITING_FOR_DETAILS) {
            history.push('/team');
            return;
        }
    }, [currentUser, loadedBrands, organization]);

    const renderLoader = () => {
        return (
            <LoaderOverlay
                className="loaderFullScreenInitializeApp1"
                title=""
            >
                <div
                    className="welcomeToSymphony"
                    style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: 20,
                        marginTop: 10,
                    }}
                >
                    Welcome to Symphony
                </div>
            </LoaderOverlay>
        )
    }

    const renderMainApp = () => {
        return (
            <div
                className="initializeAppComponent"
                style={styx(
                    sty.initializeAppComponent,
                    {
                        height: cssCalc()
                    }
                )}
            >
                <UpgradeModalProvider>
                        <NavbarProvider>
                            <Navbar className="navbarFromInitializeApp" />
                            <PostAuthRoutes />
                        </NavbarProvider>
                </UpgradeModalProvider>
            </div>
        )
    }

    if (currentUser && loadedBrands && isOrganizationInitialized) {
        return renderMainApp()
    }

    return renderLoader();
};

const sty: Record<string, CSSProperties> = {
    initializeAppComponent: {
        display: "flex",
        width: "100%",
    }
}

export default InitializeApp;